import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FolderOpen,
  File,
  FileText,
  Image,
  Download,
  Search,
  RefreshCw,
  Upload,
  Trash2,
  X,
  BarChart3,
  Target,
  Palette,
  ChevronRight,
  Clock,
  User,
  Grid,
  List,
  CloudUpload,
  CheckCircle2,
  AlertCircle,
  XCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const categoryConfig = {
  reports: { label: 'Reports & Analytics', icon: BarChart3, color: 'blue', bg: 'bg-blue-100', text: 'text-blue-600' },
  creatives: { label: 'Creative Assets', icon: Image, color: 'pink', bg: 'bg-pink-100', text: 'text-pink-600' },
  contracts: { label: 'Contracts & Agreements', icon: FileText, color: 'amber', bg: 'bg-amber-100', text: 'text-amber-600' },
  brand_assets: { label: 'Brand Assets', icon: Palette, color: 'purple', bg: 'bg-purple-100', text: 'text-purple-600' },
  proposals: { label: 'Proposals & Strategies', icon: Target, color: 'green', bg: 'bg-green-100', text: 'text-green-600' },
  other: { label: 'Other Files', icon: File, color: 'slate', bg: 'bg-slate-100', text: 'text-slate-600' }
};

// Allowed file types
const ALLOWED_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.zip', '.mp4', '.mov', '.ppt', '.pptx'];
const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB

export default function ClientProjectFilesView({ client, clientAssets = [], onRefresh }) {
  const [projectFiles, setProjectFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [categoryCounts, setCategoryCounts] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedFile, setSelectedFile] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const fileInputRef = useRef(null);
  
  // Drag & drop state
  const [isDragging, setIsDragging] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const dragCounterRef = useRef(0);

  useEffect(() => {
    fetchProjectFiles();
  }, [activeCategory]);

  const fetchProjectFiles = async () => {
    setLoading(true);
    try {
      const url = activeCategory === 'all' 
        ? `${API}/api/client/project-files`
        : `${API}/api/client/project-files?category=${activeCategory}`;
      
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setProjectFiles(data.files || []);
        setCategoryCounts(data.category_counts || {});
      }
    } catch (err) {
      console.error('Error fetching project files:', err);
    } finally {
      setLoading(false);
    }
  };

  // Drag & Drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounterRef.current = 0;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      processFiles(files);
    }
  }, []);

  // Validate and process files
  const validateFile = (file) => {
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return { valid: false, error: `File type ${ext} not allowed` };
    }
    
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: 'File exceeds 25MB limit' };
    }
    
    return { valid: true };
  };

  const processFiles = (files) => {
    const processedFiles = files.map(file => {
      const validation = validateFile(file);
      return {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file,
        name: file.name,
        size: file.size,
        status: validation.valid ? 'pending' : 'error',
        error: validation.error || null,
        progress: 0
      };
    });

    setUploadQueue(processedFiles);
    setShowUploadModal(true);
  };

  // Handle file input change
  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      processFiles(files);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Upload single file
  const uploadFile = async (queueItem) => {
    const { id, file } = queueItem;
    
    setUploadProgress(prev => ({ ...prev, [id]: 0 }));
    setUploadQueue(prev => prev.map(item => 
      item.id === id ? { ...item, status: 'uploading' } : item
    ));

    const formData = new FormData();
    formData.append('file', file);
    formData.append('asset_type', 'other');

    try {
      const response = await fetch(`${API}/api/client/assets/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        setUploadQueue(prev => prev.map(item => 
          item.id === id ? { ...item, status: 'success', progress: 100 } : item
        ));
        setUploadProgress(prev => ({ ...prev, [id]: 100 }));
      } else {
        const data = await response.json();
        setUploadQueue(prev => prev.map(item => 
          item.id === id ? { ...item, status: 'error', error: data.detail || 'Upload failed' } : item
        ));
      }
    } catch (error) {
      setUploadQueue(prev => prev.map(item => 
        item.id === id ? { ...item, status: 'error', error: 'Network error' } : item
      ));
    }
  };

  // Upload all pending files
  const uploadAllFiles = async () => {
    const pendingFiles = uploadQueue.filter(item => item.status === 'pending');
    
    for (const item of pendingFiles) {
      await uploadFile(item);
    }
    
    // Refresh after all uploads
    onRefresh && onRefresh();
    fetchProjectFiles();
  };

  // Remove file from queue
  const removeFromQueue = (id) => {
    setUploadQueue(prev => prev.filter(item => item.id !== id));
  };

  // Close upload modal
  const closeUploadModal = () => {
    setShowUploadModal(false);
    setUploadQueue([]);
    setUploadProgress({});
  };

  const handleDelete = async (assetId) => {
    setDeleting(assetId);
    try {
      const response = await fetch(`${API}/api/client/assets/${assetId}`, {
        method: 'DELETE',
        credentials: 'include'
      });


      if (response.ok) {
        onRefresh && onRefresh();
      }
    } catch (error) {
      console.error('Error deleting file:', error);
    } finally {
      setDeleting(null);
    }
  };

  const trackDownload = async (fileId) => {
    try {
      await fetch(`${API}/api/client/project-files/${fileId}/download`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (err) {
      console.error('Error tracking download:', err);
    }
  };

  const getFileIcon = (ext) => {
    if (['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'].includes(ext)) return Image;
    if (['.pdf', '.doc', '.docx'].includes(ext)) return FileText;
    if (['.xls', '.xlsx'].includes(ext)) return BarChart3;
    if (['.ppt', '.pptx'].includes(ext)) return Target;
    if (['.ai', '.psd', '.eps'].includes(ext)) return Palette;
    return File;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Combine and filter files
  const allFiles = [
    ...projectFiles.map(f => ({ ...f, source: 'project' })),
    ...(clientAssets || []).map(a => ({ 
      ...a, 
      source: 'client',
      file_id: a.asset_id,
      uploaded_at: a.uploaded_at,
      uploaded_by_name: a.uploaded_by === 'client' ? 'You' : a.uploaded_by_name
    }))
  ];

  const filteredFiles = allFiles.filter(f => {
    if (searchQuery) {
      return f.name?.toLowerCase().includes(searchQuery.toLowerCase());
    }
    return true;
  });

  const totalProjectFiles = Object.values(categoryCounts).reduce((a, b) => a + b, 0);

  if (loading && projectFiles.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div 
      className="space-y-6" 
      data-testid="client-project-files-view"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drag Overlay */}
      <AnimatePresence>
        {isDragging && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-blue-600/20 backdrop-blur-sm z-50 flex items-center justify-center"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              className="bg-white rounded-3xl p-12 shadow-2xl border-4 border-dashed border-blue-500 text-center"
            >
              <CloudUpload className="w-20 h-20 text-blue-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-slate-800 mb-2">Drop files here</h3>
              <p className="text-slate-500">Release to upload your files</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Project Files</h2>
          <p className="text-slate-500">Files shared by your team and your uploads</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileInputChange}
            className="hidden"
            accept=".png,.jpg,.jpeg,.pdf,.doc,.docx,.xls,.xlsx,.zip,.mp4,.mov,.ppt,.pptx"
            multiple
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl transition-colors shadow-lg shadow-blue-600/25"
            data-testid="upload-file-btn"
          >
            <Upload className="w-4 h-4" />
            Upload Files
          </button>
        </div>
      </div>

      {/* Drag & Drop Zone */}
      <div 
        className="border-2 border-dashed border-slate-300 hover:border-blue-400 rounded-2xl p-8 text-center transition-colors cursor-pointer bg-slate-50/50 hover:bg-blue-50/50"
        onClick={() => fileInputRef.current?.click()}
        data-testid="drop-zone"
      >
        <CloudUpload className="w-12 h-12 text-slate-400 mx-auto mb-3" />
        <p className="text-slate-600 font-medium">Drag and drop files here</p>
        <p className="text-sm text-slate-400 mt-1">or click to browse • Max 25MB per file</p>
        <p className="text-xs text-slate-400 mt-2">
          Supported: PNG, JPG, PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, ZIP, MP4, MOV
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-2xl font-bold text-slate-800">{allFiles.length}</p>
          <p className="text-xs text-slate-500">Total Files</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-2xl font-bold text-blue-600">{totalProjectFiles}</p>
          <p className="text-xs text-blue-600">From Your Team</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <p className="text-2xl font-bold text-green-600">{clientAssets?.length || 0}</p>
          <p className="text-xs text-green-600">Your Uploads</p>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
          <p className="text-2xl font-bold text-purple-600">{Object.keys(categoryCounts).filter(k => categoryCounts[k] > 0).length}</p>
          <p className="text-xs text-purple-600">Categories</p>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setActiveCategory('all')}
          className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
            activeCategory === 'all'
              ? 'bg-slate-800 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          All Files
          <span className="ml-1.5 px-1.5 py-0.5 bg-white/20 rounded-md text-xs">{allFiles.length}</span>
        </button>
        {Object.entries(categoryConfig).map(([key, config]) => {
          const count = categoryCounts[key] || 0;
          if (count === 0 && activeCategory !== key) return null;
          const Icon = config.icon;
          return (
            <button
              key={key}
              onClick={() => setActiveCategory(key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                activeCategory === key
                  ? `${config.bg} ${config.text}`
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <Icon className="w-4 h-4" />
              {config.label}
              <span className={`px-1.5 py-0.5 rounded-md text-xs ${
                activeCategory === key ? 'bg-white/30' : 'bg-slate-200'
              }`}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Search & View Toggle */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search files..."
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
          />
        </div>
        <div className="flex bg-slate-100 rounded-xl p-1">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
          >
            <Grid className="w-4 h-4 text-slate-600" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
          >
            <List className="w-4 h-4 text-slate-600" />
          </button>
        </div>
      </div>

      {/* Files Display */}
      {filteredFiles.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
          <FolderOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">
            {searchQuery ? 'No files found' : activeCategory === 'all' ? 'No files yet' : `No ${categoryConfig[activeCategory]?.label || 'files'}`}
          </h3>
          <p className="text-slate-500">
            {searchQuery 
              ? 'Try a different search term'
              : 'Files shared by your team will appear here'}
          </p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredFiles.map(file => {
            const FileIcon = getFileIcon(file.file_extension);
            const category = file.category ? categoryConfig[file.category] : null;
            const isClientFile = file.source === 'client';

            return (
              <motion.div
                key={file.file_id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                whileHover={{ y: -2 }}
                className="bg-white border border-slate-200 rounded-2xl p-4 hover:shadow-lg transition-all group cursor-pointer"
                onClick={() => setSelectedFile(file)}
                data-testid={`file-card-${file.file_id}`}
              >
                {/* Category Badge */}
                {category && (
                  <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium mb-3 ${category.bg} ${category.text}`}>
                    <category.icon className="w-3 h-3" />
                    {category.label}
                  </div>
                )}
                {isClientFile && (
                  <div className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium mb-3 bg-green-100 text-green-600">
                    Your Upload
                  </div>
                )}

                {/* File Icon */}
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-3 ${
                  category ? category.bg : 'bg-slate-100'
                }`}>
                  <FileIcon className={`w-7 h-7 ${category ? category.text : 'text-slate-500'}`} />
                </div>

                {/* File Info */}
                <h4 className="font-medium text-slate-800 truncate mb-1">{file.name}</h4>
                <p className="text-xs text-slate-500 mb-2 line-clamp-2">
                  {file.description || `Uploaded ${formatDate(file.uploaded_at)}`}
                </p>

                {/* Meta */}
                <div className="flex items-center justify-between text-xs text-slate-400">
                  <span>{formatFileSize(file.file_size)}</span>
                  <span className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {file.uploaded_by_name || file.uploaded_by}
                  </span>
                </div>

                {/* Hover Actions */}
                <div className="flex gap-2 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  <a
                    href={`${API}${file.file_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isClientFile) trackDownload(file.file_id);
                    }}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-xl transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Download
                  </a>
                  {isClientFile && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(file.asset_id);
                      }}
                      disabled={deleting === file.asset_id}
                      className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                    >
                      {deleting === file.asset_id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : (
        /* List View */
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {filteredFiles.map((file, idx) => {
            const FileIcon = getFileIcon(file.file_extension);
            const category = file.category ? categoryConfig[file.category] : null;
            const isClientFile = file.source === 'client';

            return (
              <div
                key={file.file_id}
                className={`flex items-center gap-4 p-4 hover:bg-slate-50 cursor-pointer transition-colors ${
                  idx !== 0 ? 'border-t border-slate-100' : ''
                }`}
                onClick={() => setSelectedFile(file)}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  category ? category.bg : 'bg-slate-100'
                }`}>
                  <FileIcon className={`w-6 h-6 ${category ? category.text : 'text-slate-500'}`} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-slate-800 truncate">{file.name}</h4>
                    {category && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${category.bg} ${category.text}`}>
                        {category.label}
                      </span>
                    )}
                    {isClientFile && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-600">
                        Your Upload
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                    <span>{formatFileSize(file.file_size)}</span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(file.uploaded_at)}
                    </span>
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {file.uploaded_by_name || file.uploaded_by}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <a
                    href={`${API}${file.file_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isClientFile) trackDownload(file.file_id);
                    }}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <Download className="w-5 h-5" />
                  </a>
                  {isClientFile && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(file.asset_id);
                      }}
                      disabled={deleting === file.asset_id}
                      className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      {deleting === file.asset_id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  )}
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* File Detail Modal */}
      <AnimatePresence>
        {selectedFile && (
          <FileDetailModal
            file={selectedFile}
            onClose={() => setSelectedFile(null)}
            onDownload={() => {
              if (selectedFile.source !== 'client') {
                trackDownload(selectedFile.file_id);
              }
            }}
          />
        )}
      </AnimatePresence>

      {/* Upload Modal */}
      <AnimatePresence>
        {showUploadModal && (
          <UploadModal
            queue={uploadQueue}
            progress={uploadProgress}
            onUploadAll={uploadAllFiles}
            onRemove={removeFromQueue}
            onClose={closeUploadModal}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// Upload Modal Component
function UploadModal({ queue, progress, onUploadAll, onRemove, onClose }) {
  const pendingCount = queue.filter(f => f.status === 'pending').length;
  const uploadingCount = queue.filter(f => f.status === 'uploading').length;
  const successCount = queue.filter(f => f.status === 'success').length;
  const errorCount = queue.filter(f => f.status === 'error').length;
  const isUploading = uploadingCount > 0;
  const allDone = pendingCount === 0 && uploadingCount === 0;

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'error': return <XCircle className="w-5 h-5 text-red-500" />;
      case 'uploading': return <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />;
      default: return <CloudUpload className="w-5 h-5 text-slate-400" />;
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col shadow-2xl"
        data-testid="upload-modal"
      >
        {/* Header */}
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-slate-800">Upload Files</h3>
              <p className="text-sm text-slate-500">
                {queue.length} file{queue.length !== 1 ? 's' : ''} selected
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg text-slate-400">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Progress Summary */}
          {(successCount > 0 || errorCount > 0) && (
            <div className="flex gap-4 mt-3 text-sm">
              {successCount > 0 && (
                <span className="flex items-center gap-1 text-green-600">
                  <CheckCircle2 className="w-4 h-4" />
                  {successCount} uploaded
                </span>
              )}
              {errorCount > 0 && (
                <span className="flex items-center gap-1 text-red-600">
                  <XCircle className="w-4 h-4" />
                  {errorCount} failed
                </span>
              )}
            </div>
          )}
        </div>

        {/* File List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {queue.map(item => (
            <div
              key={item.id}
              className={`flex items-center gap-3 p-3 rounded-xl border ${
                item.status === 'error' ? 'bg-red-50 border-red-200' :
                item.status === 'success' ? 'bg-green-50 border-green-200' :
                item.status === 'uploading' ? 'bg-blue-50 border-blue-200' :
                'bg-slate-50 border-slate-200'
              }`}
            >
              {/* Status Icon */}
              {getStatusIcon(item.status)}

              {/* File Info */}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-800 truncate text-sm">{item.name}</p>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <span>{formatFileSize(item.size)}</span>
                  {item.error && (
                    <span className="text-red-600">{item.error}</span>
                  )}
                </div>
                
                {/* Progress Bar */}
                {item.status === 'uploading' && (
                  <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-blue-500"
                      initial={{ width: 0 }}
                      animate={{ width: '100%' }}
                      transition={{ duration: 2 }}
                    />
                  </div>
                )}
              </div>

              {/* Remove Button */}
              {item.status === 'pending' && (
                <button
                  onClick={() => onRemove(item.id)}
                  className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 bg-slate-50 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-50"
          >
            {allDone ? 'Done' : 'Cancel'}
          </button>
          {!allDone && (
            <button
              onClick={onUploadAll}
              disabled={pendingCount === 0 || isUploading}
              className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-xl font-medium flex items-center justify-center gap-2"
              data-testid="upload-all-btn"
            >
              {isUploading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <CloudUpload className="w-4 h-4" />
                  Upload {pendingCount} File{pendingCount !== 1 ? 's' : ''}
                </>
              )}
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
}

// File Detail Modal
function FileDetailModal({ file, onClose, onDownload }) {
  const category = file.category ? categoryConfig[file.category] : null;
  const FileIcon = file.file_extension ? 
    (['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'].includes(file.file_extension) ? Image :
     ['.pdf', '.doc', '.docx'].includes(file.file_extension) ? FileText : File) : File;

  const isImage = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'].includes(file.file_extension);

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        onClick={e => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col shadow-2xl"
      >
        {/* Header */}
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
                category ? category.bg : 'bg-slate-100'
              }`}>
                <FileIcon className={`w-7 h-7 ${category ? category.text : 'text-slate-500'}`} />
              </div>
              <div>
                {category && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium mb-1 ${category.bg} ${category.text}`}>
                    <category.icon className="w-3 h-3" />
                    {category.label}
                  </span>
                )}
                <h3 className="text-lg font-bold text-slate-800 break-words">{file.name}</h3>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg text-slate-400">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Preview for images */}
          {isImage && (
            <div className="rounded-xl overflow-hidden bg-slate-100">
              <img
                src={`${API}${file.file_url}`}
                alt={file.name}
                className="w-full h-auto max-h-64 object-contain"
              />
            </div>
          )}

          {/* Description */}
          {file.description && (
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-1">Description</h4>
              <p className="text-slate-600">{file.description}</p>
            </div>
          )}

          {/* File Details */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-500">File Size</p>
              <p className="font-medium text-slate-800">{formatFileSize(file.file_size)}</p>
            </div>
            <div>
              <p className="text-slate-500">File Type</p>
              <p className="font-medium text-slate-800">{file.file_extension?.replace('.', '').toUpperCase() || 'Unknown'}</p>
            </div>
            <div>
              <p className="text-slate-500">Uploaded By</p>
              <p className="font-medium text-slate-800">{file.uploaded_by_name || file.uploaded_by}</p>
            </div>
            <div>
              <p className="text-slate-500">Upload Date</p>
              <p className="font-medium text-slate-800">
                {file.uploaded_at ? new Date(file.uploaded_at).toLocaleDateString() : 'Unknown'}
              </p>
            </div>
            {file.downloads !== undefined && (
              <div>
                <p className="text-slate-500">Downloads</p>
                <p className="font-medium text-slate-800">{file.downloads}</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 bg-slate-50 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-50"
          >
            Close
          </button>
          <a
            href={`${API}${file.file_url}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onDownload}
            className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium flex items-center justify-center gap-2"
          >
            <Download className="w-4 h-4" />
            Download
          </a>
        </div>
      </motion.div>
    </div>
  );
}
